import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { ArrowDown, ArrowUp, Download } from 'lucide-react';
import { formatCurrency } from '@/lib/format';
import i18n from '@/lib/i18n';
import { useFileDownload } from '@/hooks/useFileDownload';
import {
  buildRevenueCsvUrl,
  usePlatformLeaderboard,
  usePlatformOverview,
  usePlatformRevenue,
  type PlatformOperatorLeaderboardRow,
  type PlatformRevenueParams,
} from '@/api/hooks/usePlatformReports';
import { usePlatformTenants } from '@/api/hooks/usePlatformTenants';

type DateRangePreset = '30d' | '3m' | '6m' | '1y' | 'all';

/**
 * Platform-wide reports dashboard. Three sections stacked vertically:
 *
 * <ol>
 *   <li>Overview cards — tenants / bookings / revenue / fees / infra.</li>
 *   <li>Revenue time-series chart + table with date-range / groupBy /
 *       operator filter + CSV export.</li>
 *   <li>Operator leaderboard — ranked list of every tenant.</li>
 * </ol>
 */
export default function PlatformReportsPage() {
  const navigate = useNavigate();

  const [preset, setPreset] = useState<DateRangePreset>('6m');
  const [groupBy, setGroupBy] = useState<'DAY' | 'WEEK' | 'MONTH'>('MONTH');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [leaderboardSort, setLeaderboardSort] = useState<'revenue' | 'bookings' | 'fees'>('revenue');
  const [leaderboardDir, setLeaderboardDir] = useState<'asc' | 'desc'>('desc');

  const { startDate, endDate } = useMemo(() => datesFor(preset), [preset]);

  const revenueParams: PlatformRevenueParams = {
    startDate,
    endDate,
    groupBy,
    tenantId: tenantFilter || undefined,
  };

  const overviewQuery = usePlatformOverview();
  const revenueQuery = usePlatformRevenue(revenueParams);
  const leaderboardQuery = usePlatformLeaderboard(leaderboardSort, leaderboardDir);
  const tenantsQuery = usePlatformTenants({
    page: 0,
    size: 100,
    sortBy: 'name',
    sortDir: 'asc',
  });

  const { downloadFile, isDownloading } = useFileDownload();

  const handleExport = () => {
    const url = buildRevenueCsvUrl(revenueParams);
    downloadFile(url, `rekplay-revenue-${startDate}-to-${endDate}.csv`);
  };

  const overview = overviewQuery.data;
  const revenueRows = revenueQuery.data ?? [];
  const leaderboard = leaderboardQuery.data ?? [];
  const tenants = tenantsQuery.data?.tenants ?? [];

  const hasData = revenueRows.some((r) => Number(r.grossRevenue ?? 0) > 0);

  const toggleLeaderboardSort = (field: 'revenue' | 'bookings' | 'fees') => {
    if (leaderboardSort === field) {
      setLeaderboardDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setLeaderboardSort(field);
      setLeaderboardDir('desc');
    }
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
          Platform Admin
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
          Reports
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Cross-tenant business intelligence. Refreshes every minute.
        </p>
      </header>

      {/* ── Overview cards ───────────────────────────────── */}
      <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <OverviewCard
          label="Operators"
          value={overview ? String(overview.totalTenants) : '—'}
          sub={
            overview
              ? `${overview.activeTenants} active · ${overview.pendingTenants} pending`
              : ' '
          }
        />
        <OverviewCard
          label="Bookings"
          value={overview ? overview.totalBookings.toLocaleString() : '—'}
          sub={
            overview
              ? `${overview.bookingsToday} today · ${overview.bookingsThisWeek} this week`
              : ' '
          }
        />
        <OverviewCard
          label="Revenue (GBV)"
          value={overview ? formatCurrency(Number(overview.totalGBV), 'CAD', i18n.language) : '—'}
          sub={
            overview
              ? `${formatCurrency(Number(overview.gbvThisMonth), 'CAD', i18n.language)} this month`
              : ' '
          }
        />
        <OverviewCard
          label="Platform Fees"
          value={overview ? formatCurrency(Number(overview.totalPlatformFees), 'CAD', i18n.language) : '—'}
          sub={
            overview
              ? `${formatCurrency(Number(overview.platformFeesThisMonth), 'CAD', i18n.language)} this month`
              : ' '
          }
          accent
        />
        <OverviewCard
          label="Infrastructure"
          value={overview ? `${overview.totalUnits} units` : '—'}
          sub={
            overview
              ? `${overview.onlineUnits} online · ${overview.totalLocations} locations`
              : ' '
          }
        />
      </section>

      {/* ── Revenue chart + table ────────────────────────── */}
      <section className="mt-8 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
        <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Revenue</h2>
            <p className="text-xs text-neutral-500">
              Gross booking value + platform fees over time.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSelect
              label="Range"
              value={preset}
              onChange={(v) => setPreset(v as DateRangePreset)}
              options={[
                { value: '30d', label: 'Last 30 days' },
                { value: '3m', label: 'Last 3 months' },
                { value: '6m', label: 'Last 6 months' },
                { value: '1y', label: 'Last year' },
                { value: 'all', label: 'All time' },
              ]}
            />
            <FilterSelect
              label="Group by"
              value={groupBy}
              onChange={(v) => setGroupBy(v as 'DAY' | 'WEEK' | 'MONTH')}
              options={[
                { value: 'DAY', label: 'Day' },
                { value: 'WEEK', label: 'Week' },
                { value: 'MONTH', label: 'Month' },
              ]}
            />
            <FilterSelect
              label="Operator"
              value={tenantFilter}
              onChange={setTenantFilter}
              options={[
                { value: '', label: 'All operators' },
                ...tenants.map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
            <button
              type="button"
              onClick={handleExport}
              disabled={isDownloading}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              {isDownloading ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </header>

        <div className="mt-5 h-72">
          {revenueQuery.isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-neutral-400">
              Loading…
            </div>
          ) : hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueRows} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
                />
                <Tooltip
                  formatter={(value) => `$${Number(value).toLocaleString()}`}
                />
                <Legend />
                <Bar dataKey="grossRevenue" name="Revenue" fill="#C2713A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="platformFees" name="Platform Fees" fill="#110D09" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyRevenueState />
          )}
        </div>

        <div className="mt-5 overflow-x-auto rounded-md border border-neutral-100">
          <table className="min-w-full divide-y divide-neutral-100 text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Period</th>
                <th scope="col" className="px-3 py-2 text-right">Bookings</th>
                <th scope="col" className="px-3 py-2 text-right">Gross Revenue</th>
                <th scope="col" className="px-3 py-2 text-right">Platform Fees</th>
                <th scope="col" className="px-3 py-2 text-right">Net to Operators</th>
                <th scope="col" className="px-3 py-2 text-right">Active Tenants</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 bg-white">
              {revenueRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-xs text-neutral-400"
                  >
                    No data for this range.
                  </td>
                </tr>
              ) : (
                revenueRows.map((r) => (
                  <tr key={r.period}>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-neutral-700">
                      {r.period}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-900">
                      {r.bookingCount.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-900">
                      {formatCurrency(Number(r.grossRevenue), 'CAD', i18n.language)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[#C2713A]">
                      {formatCurrency(Number(r.platformFees), 'CAD', i18n.language)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-900">
                      {formatCurrency(Number(r.netToOperators), 'CAD', i18n.language)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-700">
                      {r.activeTenants}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Operator leaderboard ─────────────────────────── */}
      <section className="mt-8 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
        <header className="flex items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Operator leaderboard</h2>
            <p className="text-xs text-neutral-500">Ranked by {sortLabel(leaderboardSort)}.</p>
          </div>
        </header>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-100 text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Operator</th>
                <th className="px-3 py-2 text-left">Status</th>
                <SortableHeader
                  label="Bookings"
                  active={leaderboardSort === 'bookings'}
                  dir={leaderboardDir}
                  onClick={() => toggleLeaderboardSort('bookings')}
                  align="right"
                />
                <SortableHeader
                  label="Revenue"
                  active={leaderboardSort === 'revenue'}
                  dir={leaderboardDir}
                  onClick={() => toggleLeaderboardSort('revenue')}
                  align="right"
                />
                <SortableHeader
                  label="Fees generated"
                  active={leaderboardSort === 'fees'}
                  dir={leaderboardDir}
                  onClick={() => toggleLeaderboardSort('fees')}
                  align="right"
                />
                <th className="px-3 py-2 text-right">Fee rate</th>
                <th className="px-3 py-2 text-right">Locations</th>
                <th className="px-3 py-2 text-right">Units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 bg-white">
              {leaderboardQuery.isLoading ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-xs text-neutral-400">
                    Loading…
                  </td>
                </tr>
              ) : leaderboard.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-sm text-neutral-500">
                    No operators yet.
                    <div className="mt-2 text-xs">
                      <Link to="/platform/onboarding" className="text-[#C2713A] hover:underline">
                        Onboard your first operator →
                      </Link>
                    </div>
                  </td>
                </tr>
              ) : (
                leaderboard.map((row, i) => (
                  <LeaderboardRow
                    key={row.tenantId}
                    rank={i + 1}
                    row={row}
                    onClick={() => navigate(`/platform/tenants/${row.tenantId}`)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function OverviewCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        'rounded-xl border p-4 ' +
        (accent
          ? 'border-[#C2713A]/40 bg-[#C2713A]/5'
          : 'border-neutral-200 bg-white')
      }
    >
      <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={'mt-1 font-mono text-2xl font-semibold ' + (accent ? 'text-[#C2713A]' : 'text-neutral-900')}>
        {value}
      </p>
      <p className="mt-0.5 text-xs text-neutral-500">{sub || ' '}</p>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="flex flex-col text-xs font-medium text-neutral-500">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortableHeader({
  label,
  active,
  dir,
  onClick,
  align,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align: 'left' | 'right';
}) {
  return (
    <th className={'px-3 py-2 ' + (align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={onClick}
        className={
          'inline-flex items-center gap-1 transition-colors ' +
          (active ? 'text-[#C2713A]' : 'hover:text-neutral-700')
        }
      >
        {label}
        {active &&
          (dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

function LeaderboardRow({
  rank,
  row,
  onClick,
}: {
  rank: number;
  row: PlatformOperatorLeaderboardRow;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer transition-colors hover:bg-neutral-50"
    >
      <td className="whitespace-nowrap px-3 py-2 font-mono text-neutral-400">#{rank}</td>
      <td className="whitespace-nowrap px-3 py-2">
        <p className="font-medium text-neutral-900">{row.name}</p>
        <p className="font-mono text-xs text-neutral-400">{row.slug}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-900">
        {row.totalBookings.toLocaleString()}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-900">
        {formatCurrency(Number(row.totalRevenue ?? 0), 'CAD', i18n.language)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[#C2713A]">
        {formatCurrency(Number(row.platformFeesGenerated ?? 0), 'CAD', i18n.language)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-700">
        {row.platformFeePercent != null
          ? `${Number(row.platformFeePercent).toFixed(2)}%`
          : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-700">
        {row.locationCount}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-700">
        {row.unitCount}
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    SUSPENDED: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = styles[status] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function EmptyRevenueState() {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center text-sm text-neutral-500">
      <p className="font-medium text-neutral-700">No booking data yet</p>
      <p className="mt-1 max-w-sm text-xs">
        Revenue reports will populate as operators process bookings.
      </p>
      <Link
        to="/platform/onboarding"
        className="mt-3 inline-flex items-center rounded-md bg-[#C2713A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#a95f2f]"
      >
        Onboard an operator →
      </Link>
    </div>
  );
}

function sortLabel(sort: 'revenue' | 'bookings' | 'fees'): string {
  switch (sort) {
    case 'bookings':
      return 'total bookings';
    case 'fees':
      return 'platform fees generated';
    default:
      return 'total revenue';
  }
}

function datesFor(preset: DateRangePreset): { startDate: string; endDate: string } {
  const today = new Date();
  const end = fmt(today);
  const start = new Date(today);
  switch (preset) {
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '1y':
      start.setFullYear(start.getFullYear() - 1);
      break;
    case 'all':
      start.setFullYear(start.getFullYear() - 10);
      break;
  }
  return { startDate: fmt(start), endDate: end };
}

function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
